import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AppContext from '../../../Context/AppContext';
import { resolvePostOnboardingDashboard } from '../../../util/portalV2';
import DailyStreakTrackerRing from './DailyStreakTrackerRing';
import StreakCelebrateV2 from './StreakCelebrateV2';
import './Step3TrackerV2.css';

const SEGMENTS = 3;

const COACH = [
    'Tap the center button to complete your first action.',
    'Nice start. Complete two more actions to earn your streak.',
    'Almost there. One more action completes your daily streak.',
    'Daily streak earned. This is how momentum is built.',
];

/**
 * Portal V2 — practice the daily Action Tracker (3 clicks = streak).
 * Classic OolaButton stays on /steps.
 */
export default function Step3TrackerV2({ handleChangeHeader, mobile }) {
    const history = useHistory();
    const { checkUserSession } = useContext(AppContext);
    const currUser = checkUserSession() || {};
    const dashboardPath = resolvePostOnboardingDashboard();
    const [done, setDone] = useState(0);
    const [complete, setComplete] = useState(false);
    const [pulse, setPulse] = useState(0);

    useEffect(() => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }, []);

    const goDashboard = () => history.push(dashboardPath);

    const clickTracker = () => {
        if (done >= SEGMENTS || complete) return;
        const next = done + 1;
        setDone(next);
        setPulse((p) => p + 1);
        if (next === SEGMENTS) {
            window.setTimeout(() => {
                setComplete(true);
                handleChangeHeader?.(0);
            }, 700);
        }
    };

    const chipState = (n) => {
        if (done >= n) return 'complete';
        if (done + 1 === n) return 'next';
        return 'pending';
    };

    return (
        <section className={`pv2-trk3${complete ? ' is-complete' : ''}`}>
            <div className="pv2-trk3__card">
                {!complete ? (
                    <>
                        <header className="pv2-trk3__head">
                            <p className="pv2-trk3__eyebrow">
                                Step 3 · Daily streak
                            </p>
                            <h2 className="pv2-trk3__title">
                                Practice your daily streak
                            </h2>
                            <p className="pv2-trk3__lead">
                                <span className="pv2-trk3__copy-desk">
                                    This is the tracker you’ll use every day.
                                    Tap 3 times to mark three actions complete
                                    and earn your daily streak.
                                </span>
                                <span className="pv2-trk3__copy-mob">
                                    Tap 3 times to earn today’s streak.
                                </span>
                            </p>
                        </header>

                        <div className="pv2-trk3__stage">
                            <DailyStreakTrackerRing
                                totalActions={SEGMENTS}
                                completedActions={done}
                                onTap={clickTracker}
                                size="training"
                                pulseKey={pulse}
                                showGlow={false}
                            />

                            <div
                                className={`pv2-trk3__progress${
                                    done > 0 ? ' is-active' : ''
                                }`}
                                aria-live="polite"
                            >
                                <strong key={done}>{done}</strong>
                                <span>
                                    {' '}
                                    of {SEGMENTS} actions completed
                                </span>
                            </div>

                            <ul className="pv2-trk3__chips">
                                {[1, 2, 3].map((n) => {
                                    const state = chipState(n);
                                    return (
                                        <li
                                            key={`chip-${n}`}
                                            className={`pv2-trk3__chip is-${state}`}
                                        >
                                            <span
                                                className="pv2-trk3__chip-badge"
                                                aria-hidden="true"
                                            >
                                                {state === 'complete' ? '✓' : n}
                                            </span>
                                            <span className="pv2-trk3__chip-copy">
                                                <strong>Action {n}</strong>
                                                <em>
                                                    {state === 'complete'
                                                        ? 'Complete'
                                                        : state === 'next'
                                                          ? 'Next action'
                                                          : 'Pending'}
                                                </em>
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>

                            <p className="pv2-trk3__coach" aria-live="polite">
                                {COACH[done]}
                            </p>
                        </div>

                        <p className="pv2-trk3__footer">
                            <span className="pv2-trk3__copy-desk">
                                You can come back tomorrow and repeat this daily
                                ritual.
                            </span>
                            <span className="pv2-trk3__copy-mob">
                                Come back tomorrow to repeat this ritual.
                            </span>
                        </p>
                    </>
                ) : (
                    <div className="pv2-trk3__done">
                        <p className="pv2-trk3__eyebrow">Daily streak earned</p>
                        <h2 className="pv2-trk3__title">
                            Daily streak earned
                        </h2>
                        <p className="pv2-trk3__lead">
                            Great job
                            {currUser.firstName
                                ? `, ${currUser.firstName}`
                                : ''}
                            . This is the tracker you’ll use every day to keep
                            your momentum going.
                        </p>

                        <div className="pv2-trk3__done-ring-wrap">
                            <StreakCelebrateV2 active />
                            <DailyStreakTrackerRing
                                totalActions={SEGMENTS}
                                completedActions={SEGMENTS}
                                size="training"
                                continueMode
                                showAmbient={false}
                                showGlow={false}
                                label="Tap to continue"
                                onTap={goDashboard}
                            />
                        </div>

                        <div className="pv2-trk3__progress is-ready">
                            <strong>3</strong>
                            <span> of 3 actions completed</span>
                        </div>

                        <ul className="pv2-trk3__chips">
                            {[1, 2, 3].map((n) => (
                                <li
                                    key={`done-chip-${n}`}
                                    className="pv2-trk3__chip is-complete"
                                >
                                    <span
                                        className="pv2-trk3__chip-badge"
                                        aria-hidden="true"
                                    >
                                        ✓
                                    </span>
                                    <span className="pv2-trk3__chip-copy">
                                        <strong>Action {n}</strong>
                                        <em>Complete</em>
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <div className="pv2-trk3__done-actions">
                            <button
                                type="button"
                                className="pv2-trk3__cta"
                                onClick={goDashboard}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
